import { computed, inject, Injectable, signal } from '@angular/core';
import { generateUid } from '@gametailors/web-core/misc';
import { LpMissionInfo } from '../../../typings/schema';
import { IRidPlakboekjeItem, LEERPLUS } from '../rid-game.utils';
import { RidCalendarService } from './rid-calendar.service';

const RID_SCRAPBOOK_TEMPLATE_MISSION_UID = 'di_missioninfo_jFcTSCXZJCStINpNSOLmk';

/** A stroke that is still being drawn. */
export interface RidActiveStroke {
  week: number;
  color: string | null; // null erases
  width: number;
  points: number[]; // flattened [x0, y0, x1, y1, ...]
}

@Injectable()
export class RidScrapbookService {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridCalendarService = inject(RidCalendarService);

  /** Everything in the scrapbook, bottom to top. */
  readonly items = computed(() => [...this.$lpApiService.state().plakboekje].sort((a, b) => a.zIndex - b.zIndex));

  private readonly itemsByWeek = computed(() => {
    const byWeek = new Map<number, IRidPlakboekjeItem[]>();
    for (const item of this.items()) byWeek.set(item.week, [...(byWeek.get(item.week) ?? []), item]);
    return byWeek;
  });

  /** One page per week, up to and including the current week (or the last week that has something in it). */
  readonly weekCount = computed(() =>
    Math.max(...this.items().map((item) => item.week + 1), this.$ridCalendarService.currentWeekIndex() + 1, 1),
  );

  readonly currentWeekIndex = this.$ridCalendarService.currentWeekIndex;

  // the stroke being drawn right now; only added to the save once the pointer lifts, but shared so every copy of the page shows it live
  readonly activeStroke = signal<RidActiveStroke | null>(null);

  async open() {
    const mission = this.getTemplateMission();
    await this.$lpApiService.startMission(mission, true, { dataType: 'FadeTransition', uid: generateUid() });
  }

  /** A week's items, bottom to top. */
  itemsFor(week: number): IRidPlakboekjeItem[] {
    return this.itemsByWeek().get(week) ?? [];
  }

  /** Adds the content to the week, on top of everything else. Returns the uid of the new item. */
  addItem(week: number, content: IRidPlakboekjeItem['content']): string {
    const item: IRidPlakboekjeItem = { uid: generateUid(), week, zIndex: this.nextZIndex(), content };
    this.$lpApiService.state.update((s) => ({ ...s, plakboekje: [...s.plakboekje, item] }));
    return item.uid;
  }

  updateItem(uid: string, content: IRidPlakboekjeItem['content']) {
    this.update(uid, (item) => ({ ...item, content }));
  }

  bringToFront(uid: string) {
    const zIndex = this.nextZIndex();
    this.update(uid, (item) => ({ ...item, zIndex }));
  }

  nextZIndex(): number {
    return this.$lpApiService.state().plakboekje.reduce((max, item) => Math.max(max, item.zIndex), 0) + 1;
  }

  private update(uid: string, update: (item: IRidPlakboekjeItem) => IRidPlakboekjeItem) {
    this.$lpApiService.state.update((s) => ({
      ...s,
      plakboekje: s.plakboekje.map((item) => (item.uid === uid ? update(item) : item)),
    }));
  }

  private getTemplateMission(): LpMissionInfo {
    const templateMission = this.$lpApiService.getInstancesByType('MissionInfo').find((m) => m.uid === RID_SCRAPBOOK_TEMPLATE_MISSION_UID);
    if (!templateMission) throw new Error(`Template mission '${RID_SCRAPBOOK_TEMPLATE_MISSION_UID}' not found`);

    return structuredClone(templateMission);
  }
}
